import React, { createContext, useContext, useState, useEffect } from "react"

const TaskContext = createContext(undefined)

export function TaskProvider({ children, initialTasks = null }) {
  // Helper to map 'due' to 'dueDate' for all tasks
  function mapDueToDueDate(tasks) {
    if (!Array.isArray(tasks)) {
      return [];
    }
    return tasks.map(task => ({
      ...task,
      due: task.defer_date || task.due || task.dueDate || '',
      dueDate: task.defer_date || task.due || task.dueDate || '',
    }));
  }

  const [tasks, setTasks] = useState(() => {
    
    // Always use initial tasks from backend if available
    if (initialTasks) {
      return mapDueToDueDate(initialTasks);
    }
    
    // Only use localStorage if no initial tasks provided
    if (typeof window !== 'undefined') {
      const savedTasks = localStorage.getItem('tasks')
      const parsed = savedTasks ? JSON.parse(savedTasks) : []
      return mapDueToDueDate(parsed)
    }
    return []
  })

  // Always sync with backend data when initialTasks changes
  useEffect(() => {
    
    // If we have backend tasks, always use them
    if (initialTasks) {
      setTasks(mapDueToDueDate(initialTasks));
      // Clear local storage to prevent stale data
      if (typeof window !== 'undefined') {
        localStorage.removeItem('tasks');
      }
      // Reset local modifications flag since we're syncing with backend
      setHasLocalModifications(false);
    }
  }, [initialTasks])

  const [activities, setActivities] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedActivities = localStorage.getItem('activities')
      return savedActivities ? JSON.parse(savedActivities) : []
    }
    return []
  })

  // Keep track of whether tasks have been modified locally
  const [hasLocalModifications, setHasLocalModifications] = useState(false);

  // Save tasks to localStorage only if they have local modifications
  useEffect(() => {
    if (typeof window !== 'undefined' && hasLocalModifications) {
      localStorage.setItem('tasks', JSON.stringify(tasks));
    }
  }, [tasks, hasLocalModifications])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('activities', JSON.stringify(activities))
    }
  }, [activities])

  const addActivity = (activity) => {
    const newActivity = {
      ...activity,
      id: `act-${Date.now()}`,
      timestamp: new Date().toISOString()
    }
    setActivities(prev => [newActivity, ...prev].slice(0, 50)) // Keep last 50 activities
  }

  const setTasksWithLogging = (tasksOrUpdater, markAsModified = false) => {
    if (typeof tasksOrUpdater === 'function') {
      setTasks((prev) => {
        const newTasks = tasksOrUpdater(prev);
        return newTasks;
      });
    } else {
      setTasks(tasksOrUpdater);
    }
    
    if (markAsModified) {
      setHasLocalModifications(true);
    }
  };

  const addTask = (task) => {
    setTasksWithLogging((prev) => {
      // If this is a subtask, update the parent task's subtasks array
      if (task.parent_id) {
        return prev.map(t => {
          if (t.id === task.parent_id) {
            return {
              ...t,
              subtasks: [...(t.subtasks || []), {
                ...task,
                taskType: task.taskType || "standard",
                taskTypeCategories: task.taskTypeCategories || [],
                salesProbability: task.salesProbability || 0
              }],
            }
          }
          return t
        }).concat({
          ...task,
          taskType: task.taskType || "standard",
          taskTypeCategories: task.taskTypeCategories || [],
          salesProbability: task.salesProbability || 0
        })
      }
      // Otherwise, just add the task
      return [...prev, {
        ...task,
        taskType: task.taskType || "standard",
        taskTypeCategories: task.taskTypeCategories || [],
        salesProbability: task.salesProbability || 0
      }]
    }, true)
    addActivity({
      type: 'task_created',
      title: `Created task "${task.name}"`,
      user: (task.users && task.users.length > 0) ? task.users[0].name : 'You'
    })
  }

  const updateTask = (taskId, updates) => {    
    setTasksWithLogging((prev) => {      
      const task = prev.find(t => t.id === taskId)
      if (!task) {
        console.error('Task not found for taskId:', taskId);
        return prev;
      }

      // Format any date fields to ensure consistency
      const formattedUpdates = { ...updates }
      
      // Dates should already be in YYYY-MM-DD format, no need for conversion
      // Just validate the format
      if (formattedUpdates.dueDate && !formattedUpdates.dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        console.warn('Invalid dueDate format:', formattedUpdates.dueDate);
      }
      if (formattedUpdates.defer_date && !formattedUpdates.defer_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        console.warn('Invalid defer_date format:', formattedUpdates.defer_date);
      }
      
      // When deferring a task, update all date fields consistently
      if (formattedUpdates.defer_date) {
        formattedUpdates.due = formattedUpdates.defer_date;
        formattedUpdates.dueDate = formattedUpdates.defer_date;
      }
      
      // Ensure both due and dueDate are updated consistently
      if (formattedUpdates.defer_date) {
        formattedUpdates.due = formattedUpdates.defer_date;
        formattedUpdates.dueDate = formattedUpdates.defer_date;
      } else if (formattedUpdates.dueDate) {
        formattedUpdates.due = formattedUpdates.dueDate;
      } else if (formattedUpdates.due) {
        formattedUpdates.dueDate = formattedUpdates.due;
      }

      const updatedTasks = prev.map((t) => {
        if (t.id === taskId) {
          const updatedTask = { ...t, ...formattedUpdates };
          return updatedTask;
        }
        return t;
      });
      
      // Track task completion
      if (updates.status === 'Completed' && task.status !== 'Completed') {
        addActivity({
          type: 'task_completed',
          title: `Completed task "${task.name}"`,
          user: (task.users && task.users.length > 0) ? task.users[0].name : 'You'
        })
      }
      
      return updatedTasks
    }, true)
        
    // Sync with backend for certain updates (excluding optimistic notes which are handled separately)
    if (updates.notes && !updates.notes.some(note => note.isOptimistic)) {
      // Only sync notes if they're not optimistic (real notes from backend)
      // Optimistic notes are handled in the handleAddNote function
    }
  }

  const deleteTask = (taskId) => {
    setTasksWithLogging((prev) => prev.filter((task) => task.id !== taskId), true)
  }

  const getTaskStats = () => {
    const today = new Date()
    return {
      total: tasks.length,
      completed: tasks.filter((task) => task.status === "Completed").length,
      inProgress: tasks.filter((task) => task.status === "In Progress" || task.status === "In Review").length,
      overdue: tasks.filter(
        (task) =>
          (task.status === "To Do" || task.status === "In Progress" || task.status === "In Review") &&
          new Date(task.dueDate) < today
      ).length,
      notStarted: tasks.filter((task) => task.status === "To Do" && new Date(task.dueDate) >= today).length,
    }
  }

  return (
    <TaskContext.Provider
      value={{
        tasks,
        activities,
        setTasks: setTasksWithLogging,
        addTask,
        updateTask,
        deleteTask,
        getTaskStats,
      }}
    >
      {children}
    </TaskContext.Provider>
  )
}

export function useTasks() {
  const context = useContext(TaskContext)
  if (context === undefined) {
    throw new Error("useTasks must be used within a TaskProvider")
  }
  return context
} 